import React, { useState, useEffect } from 'react';
import './App.css';
import { withAuthenticator } from '@aws-amplify/ui-react';
import { useAuth } from './AuthContext';
import { fetchUserAttributes } from 'aws-amplify/auth';
import '@aws-amplify/ui-react/styles.css';

// Importaciones de componentes
import Sidebar from './components/Sidebar';
import ViewData from './components/ViewData';
import UpdateDatabase from './components/UpdateDatabase';
import UpdatePrices from './components/UpdatePrices';
import History from './components/History';
import Configuration from './components/Configuration';
import UserManagement from './components/UserManagement';
import JobStatus from './components/JobStatus';
import FeedbackMessage from './components/FeedBackMessage';

// Importaciones de servicios de API
import { generateUploadUrl, uploadFileToS3, getJobStatus, confirmMasterUpdate } from './api/services';

// Mapeo de estados para traducción
const statusTranslations = {
  loading: 'Cargando',
  processing: 'Procesando',
  pending_upload: 'Pendiente de Subida',
  pending_confirmation: 'Pendiente de Confirmación',
  awaiting_confirmation: 'Pendiente de Confirmación',
  completado: 'Completado',
  error: 'Error'
};

// Función para extraer mensaje de error de forma segura
const extractErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  if (error?.message && typeof error.message === 'string') return error.message;
  if (error?.summary && typeof error.summary === 'string') return error.summary;
  if (error?.summary && typeof error.summary === 'object') {
    return error.summary.message || JSON.stringify(error.summary);
  }
  return 'Error desconocido';
};

// Tema personalizado para el Authenticator
const authTheme = {
  name: 'dagro-theme',
  tokens: {
    colors: {
      brand: {
        primary: {
          10: { value: '#f0f7ed' },
          20: { value: '#d9ecd1' },
          40: { value: '#b3d9a3' },
          60: { value: '#93c11e' },
          80: { value: '#7aa018' },
          90: { value: '#5c7912' },
          100: { value: '#3d500c' },
        },
      },
    },
  },
};

function App({ signOut, user }) {
  const { isAdmin, isLoading: isAuthLoading } = useAuth();
  const [activeSection, setActiveSection] = useState('viewData');
  const [feedback, setFeedback] = useState({ message: '', type: '' });
  const [activeJob, setActiveJob] = useState({ id: null, message: '', status: '' });
  const [uploading, setUploading] = useState('');
  const [userEmail, setUserEmail] = useState('');

  // Obtener información del usuario
  useEffect(() => {
    const getUserInfo = async () => {
      try {
        const attributes = await fetchUserAttributes();
        setUserEmail(attributes.email || 'Usuario');
      } catch (error) {
        console.error('Error obteniendo información del usuario:', error);
        setUserEmail('Usuario');
      }
    };
    getUserInfo();
  }, [user]);

  useEffect(() => {
    if (feedback.message && !['loading', 'processing'].includes(feedback.type)) {
      const timer = setTimeout(() => setFeedback({ message: '', type: '' }), 5000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);
  
  useEffect(() => {
    let pollInterval;
    if (activeJob.id && ['processing', 'loading', 'pending_upload', 'awaiting_confirmation'].includes(activeJob.status)) {
        pollInterval = setInterval(async () => {
            try {
                const jobData = await getJobStatus(activeJob.id);
                const newStatus = (jobData.Status || '').toLowerCase();
                
                const translatedStatus = statusTranslations[newStatus] || newStatus;
                setActiveJob(prev => ({ ...prev, message: jobData.Message || `Estado: ${translatedStatus}`, status: newStatus }));
                
                if (!['processing', 'loading', 'pending_upload', 'awaiting_confirmation'].includes(newStatus)) {
                    clearInterval(pollInterval);
                    setUploading('');
                    if (newStatus === 'completado' || newStatus === 'error') {
                        setFeedback({ message: 'Proceso completado con éxito.', type: 'success' });
                        setTimeout(() => {
                            setActiveJob({ id: null, message: '', status: '' });
                        }, 5000);
                    }
                }
            } catch (pollError) {
                console.error("Error durante el sondeo:", pollError);
                setActiveJob(prev => ({ ...prev, message: 'Error de comunicación al verificar el estado.', status: 'error' }));
                clearInterval(pollInterval);
            }
        }, 3000);
    }
    return () => clearInterval(pollInterval);
  }, [activeJob.id, activeJob.status]);

  const handleJobBasedUpload = async (fileType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(fileType);
    setActiveJob({ id: null, message: 'Preparando subida segura...', status: 'loading' });

    try {
      const { uploadURL, jobId } = await generateUploadUrl(fileType, file.name);
      setActiveJob({ id: jobId, message: `Subiendo ${file.name}...`, status: 'loading' });
      await uploadFileToS3(uploadURL, file);
      setActiveJob({ id: jobId, message: 'Archivo subido. El sistema ha comenzado a procesarlo...', status: 'processing' });
    } catch (error) {
      console.error('Error en el proceso de subida asíncrona:', error);
      const errorMessage = extractErrorMessage(error);
      setActiveJob({ id: null, message: errorMessage, status: 'error' });
      setUploading('');
    } finally {
      if (event.target) event.target.value = null;
    }
  };
  
  const handleMasterUpdateConfirmation = async (decision) => {
      if (!activeJob.id) return;
      setActiveJob(prev => ({ ...prev, message: 'Procesando confirmación...', status: 'loading' }));
      try {
          await confirmMasterUpdate(activeJob.id, decision);
      } catch (error) {
          console.error("Error al confirmar la actualización:", error);
          const errorMessage = extractErrorMessage(error);
          setActiveJob(prev => ({ ...prev, message: errorMessage, status: 'error' }));
      }
  };

  const handleDismissJob = () => {
    setActiveJob({ id: null, message: '', status: '' });
    setFeedback({ message: '', type: '' });
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'viewData':
        return <ViewData setFeedback={setFeedback} />;
      case 'updateDatabase':
        return <UpdateDatabase handleFileUpload={handleJobBasedUpload} uploading={uploading} />;
      case 'updatePrices':
        return <UpdatePrices setFeedback={setFeedback} setUploading={setUploading} uploading={uploading} />;
      case 'history':
        return <History setFeedback={setFeedback} />;
      case 'userManagement':
        return <UserManagement setFeedback={setFeedback} />;
      case 'configuration':
        return <Configuration setFeedback={setFeedback} />;
      default:
        return <ViewData setFeedback={setFeedback} />;
    }
  };

  if (isAuthLoading) {
    return (
      <div className="App-loading">
        <div className="spinner"></div>
      </div>
    );
  }
    
  return (
    <div className="App">
      <Sidebar activeSection={activeSection} setActiveSection={setActiveSection} handleSignOut={signOut} isAdmin={isAdmin} />
      <div className="main-content">
        <header className="App-header">
          <h1>Plataforma de Actualización de Precios Agro Plus</h1>
          <div className="user-info">
            <span>¡Hola! {userEmail}</span>
          </div>
        </header>

        <JobStatus job={activeJob} onConfirm={handleMasterUpdateConfirmation} onDismiss={handleDismissJob} />
        <FeedbackMessage feedback={!activeJob.id ? feedback : null} />

        <main>
          {renderActiveSection()}
        </main>
      </div>
    </div>
  );
}

export default withAuthenticator(App, {
  hideSignUp: true,
  theme: authTheme,
});